import { useEffect, useMemo, useState } from "react";
import Typography from "#/components/shared/typography";
import Textarea from "#/components/shared/textarea";
import Input from "#/components/shared/input";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import { useToast } from "#/hooks/ToastContext";
import {
  ArticleAiPolicy,
  getArticleAiPolicy,
  updateArticleAiPolicy,
} from "#/services/articleAiPolicy";
import {
  ArticleMediaPolicy,
  getArticleMediaPolicy,
  updateArticleMediaPolicy,
} from "#/services/articleMediaPolicy";

type PolicyTab = "article" | "media";

export default function ArticleAiPolicyEditor() {
  const { showToast } = useToast();
  const [tab, setTab] = useState<PolicyTab>("article");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [articlePolicy, setArticlePolicy] = useState<ArticleAiPolicy | null>(null);
  const [articleOriginal, setArticleOriginal] = useState<ArticleAiPolicy | null>(null);
  const [mediaPolicy, setMediaPolicy] = useState<ArticleMediaPolicy | null>(null);
  const [mediaOriginal, setMediaOriginal] = useState<ArticleMediaPolicy | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [articleRes, mediaRes] = await Promise.all([
          getArticleAiPolicy(),
          getArticleMediaPolicy(),
        ]);
        if (!mounted) return;
        setArticlePolicy(articleRes.policy);
        setArticleOriginal(articleRes.policy);
        setMediaPolicy(mediaRes.policy);
        setMediaOriginal(mediaRes.policy);
      } catch (error: any) {
        showToast(error?.message || "Failed to load AI policies", "error");
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [showToast]);

  const articleChanged = useMemo(
    () => JSON.stringify(articlePolicy) !== JSON.stringify(articleOriginal),
    [articlePolicy, articleOriginal]
  );
  const mediaChanged = useMemo(
    () => JSON.stringify(mediaPolicy) !== JSON.stringify(mediaOriginal),
    [mediaPolicy, mediaOriginal]
  );

  if (loading || !articlePolicy || !mediaPolicy) {
    return <Typography variant="body2" className="text-brown-600">Loading policies…</Typography>;
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2">
        <ActionButton tone={tab === "article" ? "primary" : "secondary"} onClick={() => setTab("article")}>Article Policy</ActionButton>
        <ActionButton tone={tab === "media" ? "primary" : "secondary"} onClick={() => setTab("media")}>Media Policy</ActionButton>
      </div>

      {tab === "article" ? (
        <div className="flex flex-col gap-4">
          <Input label="Policy key" id="article-policy-key" value={articlePolicy.policyKey} onChange={(e) => setArticlePolicy({ ...articlePolicy, policyKey: e.target.value })} fullWidth />
          <Input label="Audience" id="article-policy-audience" value={articlePolicy.audience} onChange={(e) => setArticlePolicy({ ...articlePolicy, audience: e.target.value })} fullWidth />
          <Input label="Reading level" id="article-policy-reading" value={articlePolicy.readingLevel} onChange={(e) => setArticlePolicy({ ...articlePolicy, readingLevel: e.target.value })} fullWidth />
          <Textarea label="Generation instructions" id="article-policy-gen" value={articlePolicy.generationInstructions} onChange={(e) => setArticlePolicy({ ...articlePolicy, generationInstructions: e.target.value })} rows={5} fullWidth />
          <Textarea label="Revision instructions" id="article-policy-rev" value={articlePolicy.revisionInstructions} onChange={(e) => setArticlePolicy({ ...articlePolicy, revisionInstructions: e.target.value })} rows={5} fullWidth />
          <Textarea label="Review instructions" id="article-policy-review" value={articlePolicy.reviewInstructions || ""} onChange={(e) => setArticlePolicy({ ...articlePolicy, reviewInstructions: e.target.value })} rows={4} fullWidth />
          <div className="flex items-center gap-3">
            <ActionButton disabled={!articleChanged} isLoading={saving} onClick={async () => {
              try {
                setSaving(true);
                const res = await updateArticleAiPolicy({ policy: articlePolicy, publish: true });
                setArticlePolicy(res.policy);
                setArticleOriginal(res.policy);
                showToast("Article AI policy updated.", "success");
              } catch (e: any) {
                showToast(e?.message || "Failed to update article policy", "error");
              } finally { setSaving(false); }
            }}>Save + Publish</ActionButton>
            <ActionButton tone="secondary" onClick={() => setArticlePolicy(articleOriginal!)}>Reset</ActionButton>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <Input label="Policy key" id="media-policy-key" value={mediaPolicy.policyKey} onChange={(e) => setMediaPolicy({ ...mediaPolicy, policyKey: e.target.value })} fullWidth />
          <Textarea label="Media generation instructions" id="media-policy-gen" value={mediaPolicy.generationInstructions} onChange={(e) => setMediaPolicy({ ...mediaPolicy, generationInstructions: e.target.value })} rows={12} fullWidth />
          <Textarea label="Layout instructions" id="media-policy-layout" value={mediaPolicy.layoutInstructions} onChange={(e) => setMediaPolicy({ ...mediaPolicy, layoutInstructions: e.target.value })} rows={8} fullWidth />
          <Textarea label="Media review instructions" id="media-policy-review" value={mediaPolicy.reviewInstructions} onChange={(e) => setMediaPolicy({ ...mediaPolicy, reviewInstructions: e.target.value })} rows={6} fullWidth />
          <div className="flex items-center gap-3">
            <ActionButton disabled={!mediaChanged} isLoading={saving} onClick={async () => {
              try {
                setSaving(true);
                const res = await updateArticleMediaPolicy({ policy: mediaPolicy, publish: true });
                setMediaPolicy(res.policy);
                setMediaOriginal(res.policy);
                showToast("Media AI policy updated.", "success");
              } catch (e: any) {
                showToast(e?.message || "Failed to update media policy", "error");
              } finally { setSaving(false); }
            }}>Save + Publish</ActionButton>
            <ActionButton tone="secondary" onClick={() => setMediaPolicy(mediaOriginal!)}>Reset</ActionButton>
          </div>
        </div>
      )}
    </div>
  );
}
